/**
 * @cortex/web-api — shared browser fetch core for the SPAs (apps/web,
 * apps/admin, apps/pm).
 *
 * Before this package each app carried a byte-identical ~62-line core
 * (ApiError, ApiNetworkError, getJson/postJson/patchJson) differing only by
 * the error-message prefix ("PM API error" vs "Admin API error"), while
 * apps/web used a single `call()` wrapper with an `okStatuses` option and
 * `credentials: 'include'`. This module unifies all three shapes:
 *
 * - `ApiError` / `ApiNetworkError` — one class identity each, so
 *   `instanceof` checks work no matter which app threw. The error-message
 *   prefix is a constructor parameter.
 * - `getJson` / `postJson` / `patchJson` — the verb trio. Treats any 2xx as
 *   success (`res.ok`), throws `ApiError` otherwise. `credentials` defaults
 *   to `'same-origin'` (the admin/pm dashboards keep cookies first-party).
 * - `call()` — the apps/web shape: explicit `okStatuses` allowlist (so
 *   structured non-2xx bodies like a 401 verify response parse instead of
 *   throwing) and a JSON content-type header by default. `credentials`
 *   defaults to `'same-origin'`; apps/web binds `'include'` at its wrapper.
 *
 * No runtime response validation here — the caller's type parameter declares
 * the expected shape, exactly the contract the per-app cores had. Each app
 * keeps its own endpoint functions + response types.
 */

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    readonly status: number,
    readonly bodyText: string,
    errorPrefix = 'API error',
  ) {
    super(`${errorPrefix} ${status}`);
  }
}

function networkErrorMessage(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  if (typeof cause === 'string') return cause;
  return 'network';
}

/**
 * Thrown when `fetch` itself rejects (offline, DNS, CORS). The original
 * failure is preserved on `.cause`; `.message` carries the underlying
 * error's message so UI fallbacks that render `err.message` keep working.
 */
export class ApiNetworkError extends Error {
  override readonly name = 'ApiNetworkError';
  override readonly cause: unknown;
  constructor(cause: unknown) {
    super(networkErrorMessage(cause));
    this.cause = cause;
  }
}

export interface VerbOptions {
  /** Prefix for `ApiError.message`, e.g. "PM API error". Default "API error". */
  errorPrefix?: string;
  /** fetch credentials mode. Default 'same-origin'. */
  credentials?: RequestCredentials;
  /** Optional AbortSignal for callers that need request identity/cancelation. */
  signal?: AbortSignal;
}

export type CallOptions = RequestInit & {
  /** Statuses whose body should be parsed as the success type. Default [200, 201]. */
  okStatuses?: readonly number[];
  /** Prefix for `ApiError.message`. Default "API error". */
  errorPrefix?: string;
};

const DEFAULT_ERROR_PREFIX = 'API error';
const DEFAULT_OK_STATUSES: readonly number[] = [200, 201];

async function fetchJson<T>(path: string, init: RequestInit, errorPrefix: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  if (!res.ok) {
    throw new ApiError(res.status, await res.text().catch(() => ''), errorPrefix);
  }
  // res.json() is Promise<any>; the caller's type parameter declares the
  // expected shape (no `as` cast — ADR-0016).
  return res.json();
}

export async function getJson<T>(path: string, options: VerbOptions = {}): Promise<T> {
  const { errorPrefix = DEFAULT_ERROR_PREFIX, credentials = 'same-origin', signal } = options;
  return fetchJson<T>(path, { credentials, signal }, errorPrefix);
}

export async function postJson<T>(
  path: string,
  body: unknown,
  options: VerbOptions = {},
): Promise<T> {
  const { errorPrefix = DEFAULT_ERROR_PREFIX, credentials = 'same-origin' } = options;
  return fetchJson<T>(
    path,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials,
    },
    errorPrefix,
  );
}

export async function patchJson<T>(
  path: string,
  body: unknown,
  options: VerbOptions = {},
): Promise<T> {
  const { errorPrefix = DEFAULT_ERROR_PREFIX, credentials = 'same-origin' } = options;
  return fetchJson<T>(
    path,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials,
    },
    errorPrefix,
  );
}

/**
 * The apps/web fetch shape: JSON content-type by default, an `okStatuses`
 * allowlist for endpoints whose structured error bodies (401/422/429…) the
 * caller wants parsed rather than thrown, and pass-through of any other
 * RequestInit field. Caller-supplied fields (including `credentials` and
 * `headers`) win over the defaults — same spread order the apps/web core had.
 */
export async function call<TResp>(path: string, init: CallOptions = {}): Promise<TResp> {
  const { okStatuses = DEFAULT_OK_STATUSES, errorPrefix = DEFAULT_ERROR_PREFIX, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...(rest.headers ?? {}),
      },
      ...rest,
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  if (!okStatuses.includes(res.status)) {
    const bodyText = await res.text().catch(() => '');
    throw new ApiError(res.status, bodyText, errorPrefix);
  }
  // See fetchJson: caller-declared shape, no `as` cast.
  return res.json();
}
